// Interim access gate (until Entra ID). Probes the API once on mount: if the
// deployment has ACCESS_KEY set and no valid key is stored, shows a one-field
// code prompt before mounting the chat. With the gate off server-side, the
// probe succeeds and this component is invisible.
import { useEffect, useState, type FormEvent, type ReactNode } from 'react';
import { AccessError, getCompanies, setAccessKey } from '../lib/api';

type GateState = 'checking' | 'locked' | 'open';

export function AccessGate({ children }: { children: ReactNode }) {
  const [state, setState] = useState<GateState>('checking');
  const [code, setCode] = useState('');
  const [error, setError] = useState<string | null>(null);

  async function probe() {
    try {
      await getCompanies();
      setState('open');
    } catch (err) {
      if (err instanceof AccessError) {
        setState('locked');
      } else {
        // API down or misconfigured — not an auth problem; let the chat
        // render and surface its own error.
        setState('open');
      }
    }
  }

  useEffect(() => {
    void probe();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  async function submit(e: FormEvent) {
    e.preventDefault();
    const trimmed = code.trim();
    if (!trimmed) return;
    setAccessKey(trimmed);
    try {
      await getCompanies();
      setState('open');
    } catch (err) {
      if (err instanceof AccessError) {
        setError('Onjuiste toegangscode. Probeer opnieuw.');
      } else {
        setState('open');
      }
    }
  }

  if (state === 'checking') return null;
  if (state === 'open') return <>{children}</>;

  return (
    <div className="access-gate">
      <form className="access-card" onSubmit={submit}>
        <div className="brand">
          <span className="nina-dot" aria-hidden>⬤</span>
          <span className="brand-title">Nina</span>
        </div>
        <p className="access-hint">
          Voer de toegangscode in die je van De Graaf / Nina AI hebt gekregen.
        </p>
        {/*
          A bare type="password" made Chrome treat this as a login form and offer
          saved credentials from other *.nina-ai.com subdomains — a password
          dropdown over a shared access code nobody wants stored.

          autoComplete="off" does not help: browsers deliberately ignore it on
          password fields. "one-time-code" is the attribute that does suppress
          saved-password suggestions, and it describes what this actually is.
          The data-*-ignore attributes are the documented opt-outs for
          1Password, LastPass and Bitwarden, which ignore autocomplete entirely.
        */}
        <input
          className="access-input"
          type="password"
          name="access-code"
          autoComplete="one-time-code"
          data-1p-ignore
          data-lpignore="true"
          data-bwignore
          autoFocus
          placeholder="Toegangscode"
          value={code}
          onChange={(e) => {
            setCode(e.target.value);
            setError(null);
          }}
          aria-label="Toegangscode"
        />
        {error && <p className="access-error">{error}</p>}
        <button className="access-btn" type="submit" disabled={!code.trim()}>
          Openen
        </button>
      </form>
    </div>
  );
}
